import { MutableRefObject, useCallback } from 'react';

import {
  GridApi,
  GridRowClassNameParams,
  GridRowParams,
  GridValidRowModel,
  MuiEvent,
} from '@mui/x-data-grid-premium';

import { MAX_TREE_INDENT_DEPTH, SYSTEM_COLUMN_FIELDS } from '../GeneratikTable.constants';
import { GeneratikRowClassNameParams } from '../GeneratikTable.types';

export interface UseTableRowInteractionsParams<R extends GridValidRowModel> {
  apiRef: MutableRefObject<GridApi>;
  onRowClick?: (row: R) => void;
  onRowDoubleClick?: (row: R) => void;
  treeDataEnabled: boolean;
  groupByField?: string | null;
  externalGetCellClassName?: (params: { field: string; row: R }) => string;
  getRowClassNameProp?: (params: GeneratikRowClassNameParams<R>) => string;
}

export interface UseTableRowInteractionsReturn<R extends GridValidRowModel> {
  handleRowClick: (
    params: GridRowParams<R>,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) => void;
  getCellClassName: (params: { field: string; row: GridValidRowModel }) => string;
  handleRowDoubleClick: (params: GridRowParams<R>) => void;
  getRowClassName: (params: GridRowClassNameParams<R>) => string;
}

const SKIPPED_FIRST_COLUMN_FIELDS = new Set([...SYSTEM_COLUMN_FIELDS, 'actions']);

const resolveFirstDataField = (apiRef: MutableRefObject<GridApi>): string | undefined =>
  apiRef.current?.getVisibleColumns().find((col) => !SKIPPED_FIRST_COLUMN_FIELDS.has(col.field))
    ?.field;

const useTableRowInteractions = <R extends GridValidRowModel>({
  apiRef,
  onRowClick,
  onRowDoubleClick,
  treeDataEnabled,
  groupByField,
  externalGetCellClassName,
  getRowClassNameProp,
}: UseTableRowInteractionsParams<R>): UseTableRowInteractionsReturn<R> => {
  const handleRowClick = useCallback(
    (params: GridRowParams<R>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
      const rowNode = apiRef.current.getRowNode(params.id);
      // In treeData mode, group nodes are real data rows and should be clickable.
      // In groupBy mode, group nodes are virtual aggregation rows and should not.
      if (!onRowClick || (!treeDataEnabled && rowNode?.type === 'group')) {
        return;
      }

      const target = event.target as HTMLElement;
      const interactiveElements = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
      const isInteractive =
        interactiveElements.includes(target.tagName) ||
        target.closest('button, a, input, select, textarea, [role="button"], [role="link"]');

      if (isInteractive) {
        return;
      }

      const cell = target.closest('[data-field]');
      const clickedField = cell?.getAttribute('data-field');

      if (clickedField !== resolveFirstDataField(apiRef)) {
        return;
      }

      onRowClick(params.row);
    },
    [apiRef, onRowClick, treeDataEnabled],
  );

  const getCellClassName = useCallback(
    (params: { field: string; row: GridValidRowModel }) => {
      const classes: string[] = [];
      if (onRowClick || treeDataEnabled) {
        const isFirstDataField = params.field === resolveFirstDataField(apiRef);
        if (isFirstDataField && onRowClick) classes.push('clickable-cell');
        // The row carries the depth; this cell is the one that spends it on padding.
        if (isFirstDataField && treeDataEnabled) classes.push('GeneratikTable-treeIndentCell');
      }
      const external = externalGetCellClassName?.(params);
      if (external) classes.push(external);
      return classes.join(' ');
    },
    [apiRef, onRowClick, treeDataEnabled, externalGetCellClassName],
  );

  const handleRowDoubleClick = useCallback(
    (params: GridRowParams<R>) => {
      onRowDoubleClick?.(params.row);
    },
    [onRowDoubleClick],
  );

  const baseGetRowClassName = useCallback(
    (params: GridRowClassNameParams<R>) => {
      const classNames: string[] = [];

      if (params.id != null && apiRef.current) {
        try {
          const rowNode = apiRef.current.getRowNode(params.id);
          if (treeDataEnabled) {
            const indentLevel = Math.min(rowNode?.depth ?? 0, MAX_TREE_INDENT_DEPTH);
            if (indentLevel > 0) {
              classNames.push(`GeneratikTable-treeDepth-${indentLevel}`);
            }
          }
          if (rowNode?.type === 'group') {
            const hasChildren = (rowNode.children?.length ?? 0) > 0;
            const depth = rowNode.depth ?? 0;
            if (groupByField && depth === 0 && hasChildren) {
              classNames.push('GeneratikTable-groupRow');
              if (rowNode.childrenExpanded) {
                classNames.push('GeneratikTable-groupRow-expanded');
              }
            } else if (treeDataEnabled && depth >= 0 && hasChildren) {
              classNames.push('GeneratikTable-treeParentRow');
              if (rowNode.childrenExpanded) {
                classNames.push('GeneratikTable-treeParentRow-expanded');
              }
            }
          }
        } catch {
          // Row node not found, ignore
        }
      }

      return classNames.join(' ');
    },
    [treeDataEnabled, groupByField, apiRef],
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<R>) => {
      const augmentedParams: GeneratikRowClassNameParams<R> = {
        ...params,
        rowNode: apiRef.current?.getRowNode(params.id),
      };
      const classes = [
        baseGetRowClassName(params),
        getRowClassNameProp?.(augmentedParams)?.trim(),
      ].filter(Boolean);
      return classes.join(' ');
    },
    [baseGetRowClassName, getRowClassNameProp, apiRef],
  );

  return { handleRowClick, getCellClassName, handleRowDoubleClick, getRowClassName };
};

export default useTableRowInteractions;
